import React, { useEffect, useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
} from "@mui/material";
import dayjs from "dayjs";
import { NewsItem } from "../NewsManagement";

type Props = {
    open: boolean;
    onClose: () => void;
    onSave: (data: Partial<NewsItem>) => Promise<void>;
    initialData?: NewsItem; // 編集時のみ
};

type Errors = {
    title?: string;
    category?: string;
    contents?: string;
};

const NewsEditDialog: React.FC<Props> = ({
    open,
    onClose,
    onSave,
    initialData,
}) => {
    const [title, setTitle] = useState("");
    const [category, setCategory] = useState("");
    const [contents, setContents] = useState("");
    const [status, setStatus] = useState<NewsItem["status"]>("下書き");
    const [publishedAt, setPublishedAt] = useState("");
    const [errors, setErrors] = useState<Errors>({});

    // 初期値セット（編集時）
    useEffect(() => {
        if (initialData) {
            setTitle(initialData.title);
            setCategory(initialData.category);
            setContents(initialData.contents ?? "");
            setStatus(initialData.status);
            setPublishedAt(
                initialData.publishedAt
                    ? dayjs(initialData.publishedAt).format("YYYY-MM-DDTHH:mm")
                    : ""
            );
        } else {
            // 新規作成時
            setTitle("");
            setCategory("");
            setContents("");
            setStatus("下書き");
            setPublishedAt("");
        }
        setErrors({});
    }, [initialData, open]);


    const validate = (): boolean => {
        const newErrors: Errors = {};

        if (!title.trim()) {
            newErrors.title = "タイトルを入力してください";
        } else if (title.length > 50) {
            newErrors.title = "タイトルは50文字以内で入力してください";
        }

        if (!category.trim()) {
            newErrors.category = "カテゴリを入力してください";
        } else if (category.length > 30) {
            newErrors.category = "カテゴリは30文字以内で入力してください";
        }

        if (!contents.trim()) {
            newErrors.contents = "本文を入力してください";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };


    const handleSave = async () => {
        if (!validate()) return;

        await onSave({
            title,
            category,
            contents,
            status,
            publishedAt: publishedAt
                ? dayjs(publishedAt).toISOString()
                : null,
        });
        onClose();
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>
                {initialData ? "ニュース編集" : "ニュース新規作成"}
            </DialogTitle>

            <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
                <TextField
                    label="タイトル"
                    value={title}
                    onChange={(e) => {
                        setTitle(e.target.value);
                        if (errors.title) setErrors((prev) => ({ ...prev, title: undefined }));
                    }}
                    fullWidth
                    error={!!errors.title}
                    helperText={errors.title}

                />

                <TextField
                    label="カテゴリ"
                    value={category}
                    onChange={(e) => {
                        setCategory(e.target.value);
                        if (errors.category) setErrors((prev) => ({ ...prev, category: undefined }));
                    }}
                    fullWidth
                    error={!!errors.category}
                    helperText={errors.category}
                />

                <TextField
                    label="本文"
                    value={contents}
                    onChange={(e) => {
                        setContents(e.target.value);
                        if (errors.contents) setErrors((prev) => ({ ...prev, contents: undefined }));
                    }}
                    fullWidth
                    multiline
                    rows={4}
                    error={!!errors.contents}
                    helperText={errors.contents}

                />

                <TextField
                    label="公開日時"
                    type="datetime-local"
                    value={publishedAt}
                    onChange={(e) => setPublishedAt(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                />
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose}>キャンセル</Button>
                <Button variant="contained" onClick={handleSave}>
                    保存
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default NewsEditDialog;